/*
 * rn-cliq-mounts.js — vanilla-JS rebuild of the interactive widgets on the Replo-built
 * "Cliq Mounts" product page (Replo element b411a929-cc12-43c2-a641-76b199a9580f, product
 * 7748633460851, handle "cliq-mounts"), for the native Shopify recreation
 * (sections/rn-cliq-mounts.liquid + snippets/rn-cliq-mounts-01..05). No Replo runtime ships
 * on this page — everything below is a from-scratch reimplementation driven off the SAME
 * data-* attributes Replo left in the extracted markup (data-replo-carousel, data-replo-
 * collapsible, data-replo-option-select, data-rn-atc, etc.), scoped to .rn-cliq-mounts only.
 *
 * Add-to-cart config extracted DIRECTLY from the page's Replo runtime bundle
 * (replocdn.com/.../elementPreview/b411a929-....min.js) and VERIFIED LIVE by
 * network-intercepting /cart/add.js on the SOURCE preview page (route-mocked, no real
 * carts touched) — see BUILD-LOG.md §4 for the exact captured payloads.
 *
 * NOT reproduced 1:1: none needed on this page — unlike the Cliq main-product coverage
 * picker, this page's "Mount Type" optionSelect component IS a real Shopify-variant
 * optionSelect with static compiled CSS for every card's "Selected Option" variant state
 * (background gradient + white text + check icon), so the pixel-exact highlight is fully
 * reproducible by toggling the same className Replo itself adds/removes on selection.
 *
 * ALSO discovered live (not in the initial brief, found while diffing preview vs bench
 * screenshots): selecting a mount card also swaps the hero product-image carousel to a
 * photo of Cliq mounted that way. The page's baked default hero photo on a fresh load is
 * source-index 2 ("Slide 3 of 7"), not index 0, because Saddle Mount is the default
 * selection. Confirmed the full mapping by scripting clicks on the source preview and
 * reading the resulting data-slide-source-index (see BUILD-LOG.md §3) — wired below.
 */
(function () {
  "use strict";

  var ROOT_SELECTOR = ".rn-cliq-mounts";

  /* ============================================================
   * 1. MOUNT TYPE OPTION PICKER — [data-replo-option-select="true"]
   * ============================================================
   * Unlike the Cliq main-product's per-card coverage picker (3 separate single-option
   * listboxes), this page has ONE real `role="listbox"` per breakpoint (2 total: a
   * mobile/tablet copy and a desktop copy — only one is ever visible at a time, same
   * responsive-duplicate pattern as everything else on this page) with 4 `role="option"`
   * children in DOM order: Saddle Mount / Aero Mount / Gear Mount / Standard Mount —
   * confirmed to match the product's own option value order
   * (Admin GraphQL: gid://shopify/Product/7748633460851, option "Mount Type" values
   * ["Saddle Mount","Aero Mount","Gear Mount","Standard Mount"]).
   *
   * Each option card, when selected, gets ONE additional CSS class from Replo's compiled
   * stylesheet (the component's "Selected Option" variant — background-image: linear-
   * gradient(90deg,#205EEE,#123688), white label text, and the check-icon becomes
   * display:inline — all via nested selectors keyed off that one class, confirmed by
   * grepping the scoped stylesheet). The map below was read directly out of the captured
   * markup + the runtime bundle's componentOverrides (not guessed) — see BUILD-LOG.md §3.
   */
  var VARIANT_IDS = [42210665955443, 42210665988211, 42210666020979, 42210666053747]; // Saddle, Aero, Gear, Standard

  // data-rid (per option element) -> { index, selectedClass }
  var OPTION_MAP = {
    "7b3348e4-a88d-486d-a8f5-70d1e65ccb42": { index: 0, selectedClass: "r-1vss6tz" }, // Saddle (mobile listbox)
    "5185b2f0-3a13-4f11-8eb3-3f9ee59fea7c": { index: 1, selectedClass: "r-znzrmm" },  // Aero (mobile listbox)
    "d153243b-6347-4b57-8a65-c8c2f3b56f2d": { index: 2, selectedClass: "r-5acg32" },  // Gear (mobile listbox)
    "31e4a138-0855-4872-84f5-5dbc8fa8ae59": { index: 3, selectedClass: "r-d9arlz" },  // Standard (mobile listbox)
    "d141aecb-a527-4afe-a878-147951fc9b8b": { index: 0, selectedClass: "r-1mq6zq1" }, // Saddle (desktop listbox)
    "65be6137-8268-4e2d-83b0-b5a43f6fdebe": { index: 1, selectedClass: "r-z5hce4" },  // Aero (desktop listbox)
    "0459aeef-a24d-4af9-919a-32a5d3e34943": { index: 2, selectedClass: "r-ptwb3y" },  // Gear (desktop listbox)
    "f23c764f-a556-4f57-9922-204904f4fb04": { index: 3, selectedClass: "r-1ucyqwd" }  // Standard (desktop listbox)
  };

  var selectedMountIndex = 0; // Saddle Mount is the source page's baked default (aria-selected="true" at capture)

  /* Selecting a mount card also swaps the hero product-image carousel to a matching photo
   * of Cliq mounted that way — confirmed live on the source preview (network/DOM probe:
   * clicking each card and reading the carousel's resulting data-slide-source-index):
   *   Saddle -> source-index 2, Aero -> 4, Gear -> 5, Standard -> 6 (this is also why the
   *   page's baked DEFAULT hero image on a fresh load is source-index 2, not 0 — Saddle
   *   Mount is the default-selected card). initCarousels() (below) registers a controller
   *   per hero carousel (identified by 7 slides, matching the product's 7-image gallery)
   *   into HERO_CAROUSELS for this mapping to drive.
   */
  var HERO_SLIDE_BY_OPTION_INDEX = [2, 4, 5, 6];
  var HERO_CAROUSELS = [];

  function initMountOptionPicker() {
    var options = document.querySelectorAll(ROOT_SELECTOR + ' [data-replo-option-select="true"] [role="option"]');
    if (!options.length) return;

    function applySelection(index, opts) {
      selectedMountIndex = index;
      options.forEach(function (opt) {
        var rid = opt.getAttribute("data-rid");
        var meta = OPTION_MAP[rid];
        if (!meta) return;
        var active = meta.index === index;
        opt.setAttribute("aria-selected", active ? "true" : "false");
        opt.classList.toggle(meta.selectedClass, active);
      });
      var heroSlide = HERO_SLIDE_BY_OPTION_INDEX[index];
      HERO_CAROUSELS.forEach(function (c) {
        c.goToSourceIndex(heroSlide, opts);
      });
    }

    options.forEach(function (opt) {
      var rid = opt.getAttribute("data-rid");
      var meta = OPTION_MAP[rid];
      if (!meta) return;
      opt.addEventListener("click", function () {
        applySelection(meta.index, { silent: false });
      });
      opt.addEventListener("keydown", function (e) {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          opt.click();
        }
      });
    });

    applySelection(selectedMountIndex, { silent: true }); // sync both breakpoint copies + hero image to the default on load
  }

  /* ============================================================
   * 2. ADD TO CART — the 4 [data-rn-atc] buttons
   * ============================================================
   * Two independent actions on this page (confirmed via the runtime bundle + a live
   * network-intercepted /cart/add.js test on the source preview — BUILD-LOG.md §4):
   *
   *   a) Mount Type picker ATC (2 breakpoint dupes: "5470b3ea-mount-mobile" visible on
   *      mobile/tablet, "117a4ff5-mount-desktop" visible on desktop) — adds whichever
   *      Mount Type variant is currently selected. Verified payloads:
   *        default (Saddle): {"items":[{"id":"42210665955443","quantity":1,"properties":{}}]}
   *        after picking Aero: {"items":[{"id":"42210665988211","quantity":1,"properties":{}}]}
   *
   *   b) Sticky HELIOS ring cross-sell bar ATC (2 breakpoint dupes: "8fe18fff-ring-desktop"
   *      visible on desktop, "b4b82168-ring-mobile" visible on mobile/tablet — a
   *      position:fixed bar pinned to the bottom of the viewport that only becomes visible
   *      after scrolling past the hero). No picker of its own — always adds the SAME baked
   *      variant/selling-plan pair from the hidden <product-form> in that section (product
   *      8149795373171, "HELIOS Smart Ring (Ships September 21-28)"). Verified live payload:
   *        {"items":[{"id":"44228046618739","quantity":1,"properties":{},"selling_plan":"3151003763"}]}
   *      (selling_plan captured as a STRING on the real page, reproduced as-is here for
   *      byte-exact payload parity, though Shopify accepts either form).
   *
   *   DOCTRINE FLAG (not fixed — ported as observed): this sticky bar's ring price reads
   *   "$613.99 -> $335.99", which does not match CLAUDE.md's Current Offer block ($249
   *   standalone / $299 Cyclist Pack). Flagging for Leo, not silently corrected.
   */
  function buildRingItem() {
    return { id: 44228046618739, quantity: 1, properties: {}, selling_plan: "3151003763" };
  }

  function buildMountItem() {
    return { id: VARIANT_IDS[selectedMountIndex], quantity: 1, properties: {} };
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (e) { throw e; });
      return r.json();
    });
  }

  function openCartDrawer() {
    var cd = document.querySelector("cart-drawer");
    return postJSON("/cart/update.js", {
      updates: {},
      sections: ["cart-drawer", "cart-icon-bubble"],
      sections_url: window.location.pathname
    }).then(function (data) {
      if (!cd) return data;
      if (typeof cd.renderContents === "function") {
        cd.renderContents(data);
      } else {
        cd.classList.add("animate", "active");
      }
      try {
        var freshCd = new DOMParser()
          .parseFromString(data.sections["cart-drawer"], "text/html")
          .querySelector("cart-drawer");
        if (freshCd) cd.classList.toggle("is-empty", freshCd.classList.contains("is-empty"));
      } catch (e) {}
      return data;
    });
  }

  function initAddToCart() {
    var buttons = document.querySelectorAll(ROOT_SELECTOR + " [data-rn-atc]");
    buttons.forEach(function (btn) {
      var kind = (btn.getAttribute("data-rn-atc") || "").indexOf("ring") !== -1 ? "ring" : "mount";
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        if (btn.getAttribute("aria-disabled") === "true") return;
        btn.setAttribute("aria-disabled", "true");
        var prevCursor = btn.style.cursor;
        btn.style.cursor = "progress";
        var item = kind === "ring" ? buildRingItem() : buildMountItem();
        postJSON("/cart/add.js", { items: [item] })
          .then(function () {
            return openCartDrawer();
          })
          .catch(function (err) {
            console.error("[rn-cliq-mounts] add to cart failed", err);
          })
          .finally(function () {
            btn.removeAttribute("aria-disabled");
            btn.style.cursor = prevCursor;
          });
      });
    });
  }

  /* ============================================================
   * 3. CAROUSELS — [data-replo-carousel="true"] (3 on this page: 2 breakpoint copies of
   *    the hero image gallery + 1 shared-DOM testimonial carousel)
   * ============================================================
   * Same clone-dedup + swipe/drag engine proven on the ring/cliq builds.
   */
  function initCarousels() {
    var roots = document.querySelectorAll(ROOT_SELECTOR + ' [data-replo-carousel="true"]');
    roots.forEach(function (root) {
      var track = root.querySelector('[data-replo-part="slide-track"]');
      if (!track) return;
      var prevBtn = root.querySelector('[data-replo-part="nav-button"][data-direction="previous"]');
      var nextBtn = root.querySelector('[data-replo-part="nav-button"][data-direction="next"]');

      var rawSlides = Array.prototype.slice.call(track.querySelectorAll('[data-replo-part="slide"]'));
      if (!rawSlides.length) return;

      var seen = {};
      var order = [];
      rawSlides.forEach(function (el) {
        var idx = el.getAttribute("data-slide-source-index") || el.getAttribute("data-slide-index") || "0";
        if (!(idx in seen)) {
          seen[idx] = el;
          order.push(idx);
        } else if (el.getAttribute("data-is-target") === "true") {
          seen[idx].parentNode && seen[idx].parentNode.removeChild(seen[idx]);
          seen[idx] = el;
        } else {
          el.parentNode && el.parentNode.removeChild(el);
        }
      });
      order.sort(function (a, b) { return parseInt(a, 10) - parseInt(b, 10); });
      var slides = order.map(function (idx) { return seen[idx]; });
      Array.prototype.slice.call(track.children).forEach(function (child) {
        if (slides.indexOf(child) === -1) track.removeChild(child);
      });
      slides.forEach(function (s) { track.appendChild(s); });

      if (slides.length < 2) {
        if (prevBtn) prevBtn.style.display = "none";
        if (nextBtn) nextBtn.style.display = "none";
        return;
      }

      var slidesPerPageAboveMobile = track.style.getPropertyValue("--replo-carousel-slides-per-page") || "1";
      var mobileMq = window.matchMedia("(max-width: 640px)");
      function applySlidesPerPage() {
        track.style.setProperty("--replo-carousel-slides-per-page", mobileMq.matches ? "1" : slidesPerPageAboveMobile);
      }
      applySlidesPerPage();

      var current = 0;
      slides.forEach(function (s, i) {
        s.style.flexShrink = "0";
        s.style.width = "calc((100% + var(--replo-gap, 0px)) / var(--replo-carousel-slides-per-page, 1) - var(--replo-gap, 0px))";
        s.setAttribute("aria-hidden", i === 0 ? "false" : "true");
        s.setAttribute("tabindex", i === 0 ? "0" : "-1");
        s.setAttribute("data-is-active", i === 0 ? "true" : "false");
      });
      track.style.transitionProperty = "transform";
      track.style.transitionDuration = "380ms";
      track.style.transitionTimingFunction = "cubic-bezier(0.4, 0, 0.2, 1)";

      function gapPx() {
        var g = getComputedStyle(track).columnGap || getComputedStyle(track).gap || "0px";
        var n = parseFloat(g);
        return isNaN(n) ? 0 : n;
      }

      function goTo(index, opts) {
        var silent = opts && opts.silent;
        current = ((index % slides.length) + slides.length) % slides.length;
        var slideRect = slides[0].getBoundingClientRect();
        var step = slideRect.width + gapPx();
        track.style.transitionDuration = silent ? "0ms" : "380ms";
        track.style.transform = "translate3d(" + -1 * current * step + "px,0,0)";
        slides.forEach(function (s, i) {
          var active = i === current;
          s.setAttribute("aria-hidden", active ? "false" : "true");
          s.setAttribute("tabindex", active ? "0" : "-1");
          s.setAttribute("data-is-active", active ? "true" : "false");
        });
      }

      /* This page's hero product-image gallery always has 7 slides (confirmed via Admin +
         the captured markup's "Slide N of 7" counter) — register it so the Mount Type
         picker (section 1 above) can drive it to the matching photo on selection. The
         review-testimonial carousel (3 slides) never matches this count and is left out. */
      if (slides.length === 7) {
        HERO_CAROUSELS.push({
          goToSourceIndex: function (sourceIdx, opts) {
            var pos = order.indexOf(String(sourceIdx));
            if (pos !== -1) goTo(pos, opts);
          }
        });
      }

      if (prevBtn) prevBtn.addEventListener("click", function () { goTo(current - 1); });
      if (nextBtn) nextBtn.addEventListener("click", function () { goTo(current + 1); });

      var startX = null, deltaX = 0, dragging = false;
      track.style.touchAction = "pan-y";
      track.addEventListener("pointerdown", function (e) {
        dragging = true;
        startX = e.clientX;
        track.style.transitionDuration = "0ms";
        try { track.setPointerCapture(e.pointerId); } catch (err) {}
      });
      track.addEventListener("pointermove", function (e) {
        if (!dragging) return;
        deltaX = e.clientX - startX;
        var slideRect = slides[0].getBoundingClientRect();
        var step = slideRect.width + gapPx();
        track.style.transform = "translate3d(" + (-1 * current * step + deltaX) + "px,0,0)";
      });
      function endDrag() {
        if (!dragging) return;
        dragging = false;
        var slideRect = slides[0].getBoundingClientRect();
        var step = slideRect.width + gapPx();
        if (Math.abs(deltaX) > step * 0.2) {
          goTo(current + (deltaX < 0 ? 1 : -1));
        } else {
          goTo(current);
        }
        deltaX = 0;
      }
      track.addEventListener("pointerup", endDrag);
      track.addEventListener("pointercancel", endDrag);
      track.addEventListener("pointerleave", function () { if (dragging) endDrag(); });

      var resizeTimer;
      window.addEventListener("resize", function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          applySlidesPerPage();
          goTo(current, { silent: true });
        }, 120);
      });

      goTo(0, { silent: true });
    });
  }

  /* ============================================================
   * 4. COLLAPSIBLES / ACCORDIONS — [data-replo-collapsible="true"] (12 total: 6 in the
   *    hero buybox × 2 breakpoint dupes each = "What's Included & Thrive App Details" /
   *    "Shipping Details & Warranty" / "Mount Specifications", + 6 FAQ questions)
   * ============================================================
   * Plain hidden-attribute swap, no invented smooth-height animation (matches source).
   */
  function initCollapsibles() {
    var roots = document.querySelectorAll(ROOT_SELECTOR + ' [data-replo-collapsible="true"]');
    roots.forEach(function (root) {
      var trigger = root.querySelector('[role="button"][aria-controls]');
      if (!trigger) return;
      var panelId = trigger.getAttribute("aria-controls");
      var panel = panelId ? document.getElementById(panelId) : null;
      if (!panel) return;
      function toggle() {
        var isOpen = trigger.getAttribute("aria-expanded") === "true";
        var next = !isOpen;
        trigger.setAttribute("aria-expanded", next ? "true" : "false");
        root.setAttribute("data-state", next ? "open" : "closed");
        if (next) panel.removeAttribute("hidden");
        else panel.setAttribute("hidden", "");
      }
      trigger.addEventListener("click", toggle);
      trigger.addEventListener("keydown", function (e) {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggle();
        }
      });
    });
  }

  /* ============================================================
   * 5. STICKY HELIOS RING BAR REVEAL — [data-rid="82ac..." > "Sticky Cart"]
   * ============================================================
   * Found live while diffing preview vs bench (not obvious from static markup): the
   * section-01 sticky ring bar is 32px tall and visually empty by default at EVERY
   * breakpoint (both its desktop content block, data-rid 65f02c9f/"r-goo4f3", and its
   * mobile/tablet content block, data-rid 5088dd0e/"r-tubafx", compile to display:none in
   * the captured stylesheet) — it only reveals its price/image/ADD TO CART content once the
   * shopper scrolls roughly past the main hero buybox. Confirmed via a MutationObserver on
   * the source preview: crossing the threshold adds ONE class to each content block
   * (r-goo4f3 -> +"r-cf11ev" reveals it at desktop widths, r-tubafx -> +"r-n5zfad" reveals
   * it at mobile/tablet widths — verified visually visible/reachable at both 390 and 1440
   * after the classes are added, both classes already ship in the scoped stylesheet so no
   * new CSS is needed here). Bisected the live threshold to scrollY 1000-1100px on desktop,
   * which lines up with the main hero "ADD TO CART" button (~1099px down the document)
   * scrolling out of the viewport — reproduced generically here via IntersectionObserver on
   * the hero-buybox section's own top-level wrapper — that one element is always rendered
   * and correctly sized at every breakpoint (unlike a single breakpoint-conditional ATC
   * button, which would sit at zero size and observe unreliably at the other width),
   * toggling both reveal classes together (only one is ever visually relevant per
   * breakpoint; harmless to keep both in sync).
   */
  function initStickyRingBarReveal() {
    var desktopBlock = document.querySelector(ROOT_SELECTOR + ' [data-rid="65f02c9f-d3c5-416b-b57e-61e343e26737"]');
    var mobileBlock = document.querySelector(ROOT_SELECTOR + ' [data-rid="5088dd0e-5c8e-4dad-bb8f-043cea98f7ff"]');
    var anchor = document.querySelector(ROOT_SELECTOR + ' [data-rid="62666443-4ce3-4612-b2e4-07b7a56b84c2"]');
    if (!desktopBlock && !mobileBlock) return;
    if (!anchor || !("IntersectionObserver" in window)) return;

    function setRevealed(revealed) {
      if (desktopBlock) desktopBlock.classList.toggle("r-cf11ev", revealed);
      if (mobileBlock) mobileBlock.classList.toggle("r-n5zfad", revealed);
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          setRevealed(!entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
      },
      { root: null, threshold: 0 }
    );
    io.observe(anchor);
  }

  /* ============================================================
   * 6. ENTRANCE ANIMATIONS — Replo's "animate on scroll into view" system
   * ============================================================
   * Same generic fix proven on the ring/cliq builds: watch every element whose
   * animation-play-state is "paused" and flip it to "running" once it's within ~15% of
   * the viewport (above-the-fold elements unpause immediately on first observe).
   */
  function initEntranceAnimations() {
    var all = document.querySelectorAll(ROOT_SELECTOR + " *");
    var candidates = [];
    all.forEach(function (el) {
      var cs = getComputedStyle(el);
      if (cs.animationPlayState === "paused" && cs.animationName && cs.animationName !== "none") {
        candidates.push(el);
      }
    });
    if (!candidates.length) return;
    if (!("IntersectionObserver" in window)) {
      candidates.forEach(function (el) { el.style.animationPlayState = "running"; });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.style.animationPlayState = "running";
            io.unobserve(entry.target);
          }
        });
      },
      { root: null, rootMargin: "0px 0px -10% 0px", threshold: 0.01 }
    );
    candidates.forEach(function (el) { io.observe(el); });
  }

  function init() {
    initCarousels(); // must run before initMountOptionPicker() so HERO_CAROUSELS is populated
                      // for the default-selection sync (Saddle Mount -> hero slide index 2)
    initMountOptionPicker();
    initAddToCart();
    initCollapsibles();
    initStickyRingBarReveal();
    initEntranceAnimations();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
